import { useEffect, useMemo, type ReactNode } from 'react';
import { useCurrency } from '@/currency/store';
import { currencyMessages } from '@/currency/messages';
import { IntlProvider } from 'react-intl';
import { getMessages } from './messages';
import { INTL_TAG, type Locale } from './locales';
import { ensureChineseFont } from './fonts';

export function I18nProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const currency = useCurrency(s => s.byLocale[locale] ?? s.active);
  const messages = useMemo(() => currencyMessages(getMessages(locale), currency), [locale, currency]);

  useEffect(() => {
    document.documentElement.lang = locale;
    // tab title: the name in the UI language first — "Leyo · 乐游", zh "乐游 · Leyo"
    document.title = `${messages['common.brand']} · ${messages['common.brandAlt']}`;
    if (locale === 'zh') ensureChineseFont();
  }, [locale, messages]);

  return (
    <IntlProvider
      locale={INTL_TAG[locale]}
      defaultLocale="en-US"
      messages={messages}
      onError={(err) => {
        if (import.meta.env.DEV && import.meta.env.MODE !== 'test') console.warn(`[i18n] ${err.message}`);
      }}
    >
      {children}
    </IntlProvider>
  );
}
